import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PRIORITY_LABELS, PRIORITY_COLORS, STATUS_LABELS, CHECK_STATUS_LABELS, formatDate } from "@/lib/labels";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EvidenceGallery } from "@/components/EvidenceGallery";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip } from "recharts";
import { Sparkles, Bug, ClipboardList, AlertTriangle, Check, X, Minus, TrendingUp, TrendingDown, MoveRight, Thermometer, Droplets } from "lucide-react";

export const PGA_LEVEL_LABELS = { baixo: "Baixo", medio: "Médio", alto: "Alto", critico: "Crítico" };
export const PGA_LEVEL_COLORS = {
  baixo: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  medio: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  alto: "bg-orange-500/15 text-orange-400 border-orange-500/30",
  critico: "bg-red-500/15 text-red-400 border-red-500/30",
};
export const PGA_LEVEL_TEXT = { baixo: "text-emerald-400", medio: "text-amber-400", alto: "text-orange-400", critico: "text-red-400" };
export const PGA_LEVEL_BORDER = { baixo: "border-emerald-500/50", medio: "border-amber-500/50", alto: "border-orange-500/60", critico: "border-red-500/60" };

export function TrendIcon({ trend, size = 13 }) {
  if (trend === "subindo") return <TrendingUp size={size} className="text-red-400" />;
  if (trend === "caindo") return <TrendingDown size={size} className="text-emerald-400" />;
  return <MoveRight size={size} className="text-muted-foreground" />;
}

const CHECK_ICON = { conforme: Check, nao_conforme: X, nao_aplicavel: Minus };
const CHECK_CLS = { conforme: "text-emerald-400", nao_conforme: "text-red-400", nao_aplicavel: "text-slate-400" };
const tooltipStyle = { backgroundColor: "hsl(240,10%,10%)", border: "1px solid hsl(240,10%,20%)", borderRadius: 8, fontSize: 12, color: "#fff" };

export function StructureHistoryDialog({ structureId, onClose }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (structureId) {
      setData(null);
      api.get(`/pga/structures/${structureId}/history`).then(({ data: d }) => setData(d)).catch(() => onClose());
    }
  }, [structureId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open={!!structureId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto scrollbar-thin" data-testid="pga-history-dialog">
        <DialogHeader><DialogTitle className="font-heading">Histórico da Estrutura</DialogTitle></DialogHeader>
        {!data ? (
          <div className="py-10 flex justify-center"><div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>
        ) : (
          <div className="space-y-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="font-heading font-semibold">{data.structure.name}{data.structure.code ? ` · ${data.structure.code}` : ""}</p>
                <p className="text-xs text-muted-foreground">{data.structure.type_name}</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-center" data-testid="pga-history-index">
                  <p className={`font-heading text-3xl font-bold ${PGA_LEVEL_TEXT[data.infestation?.level] || "text-muted-foreground"}`}>{data.infestation?.index ?? "—"}</p>
                  <p className="text-[9px] uppercase tracking-wider text-muted-foreground flex items-center gap-1 justify-center"><Bug size={10} /> Índice de infestação</p>
                </div>
                {data.infestation?.level && (
                  <Badge variant="outline" className={PGA_LEVEL_COLORS[data.infestation.level]}>
                    {PGA_LEVEL_LABELS[data.infestation.level]} <span className="ml-1"><TrendIcon trend={data.infestation.trend} size={11} /></span>
                  </Badge>
                )}
              </div>
            </div>

            {data.timeline?.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Evolução (capturas × não conformidades)</p>
                <ResponsiveContainer width="100%" height={160}>
                  <ComposedChart data={data.timeline.map((t) => ({ ...t, name: String(t.date).slice(5, 10) }))}>
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: "hsl(240,5%,60%)" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: "hsl(240,5%,60%)" }} axisLine={false} tickLine={false} width={26} allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="captures" fill="#eab308" radius={[3, 3, 0, 0]} barSize={14} name="Capturas" />
                    <Line type="monotone" dataKey="nc" stroke="#ef4444" strokeWidth={2} dot={{ r: 2 }} name="NC" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}

            {data.captures?.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><Bug size={12} className="text-yellow-500" /> Capturas ({data.captures.length})</p>
                <div className="space-y-1.5 max-h-52 overflow-y-auto scrollbar-thin pr-1">
                  {data.captures.map((c) => (
                    <div key={c.id} className="rounded-md border border-border px-3 py-2 flex items-center justify-between gap-2 text-sm" data-testid={`pga-history-capture-${c.id}`}>
                      <div className="min-w-0">
                        <p className="font-medium truncate">{c.quantity}x {c.species_name}{c.detail ? ` — ${c.detail}` : ""}</p>
                        <p className="text-[11px] text-muted-foreground">{c.technician_name} · {formatDate(c.captured_at)}{c.trap_type ? ` · ${c.trap_type}` : ""}{c.location ? ` · ${c.location}` : ""}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><ClipboardList size={12} /> Monitoramentos ({data.monitorings.length})</p>
              {data.monitorings.length === 0 ? <p className="text-sm text-muted-foreground">Nenhum monitoramento registrado.</p> : (
                <div className="space-y-1.5">
                  {data.monitorings.map((m) => (
                    <div key={m.id} className="rounded-md border border-border px-3 py-2 flex items-center justify-between gap-2 text-sm">
                      <div className="min-w-0">
                        <p className="text-[11px] text-muted-foreground">{m.inspector_name} · {formatDate(m.finished_at)}</p>
                        <p className="text-xs">
                          {m.captures_total || 0} captura(s) · {m.nc_count || 0} NC
                          {m.temperature != null ? ` · ${m.temperature}°C` : ""}{m.humidity != null ? ` · ${m.humidity}% UR` : ""}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {data.occurrences?.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><AlertTriangle size={12} /> Ocorrências geradas ({data.occurrences.length})</p>
                <div className="space-y-1.5">
                  {data.occurrences.map((o) => (
                    <div key={o.id} className="rounded-md border border-border px-3 py-2 flex items-center justify-between gap-2 text-sm">
                      <div className="min-w-0">
                        <p className="font-mono text-xs">{o.protocol}</p>
                        <p className="text-xs text-muted-foreground truncate">{o.type_name}</p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Badge variant="outline" className={PRIORITY_COLORS[o.priority]}>{PRIORITY_LABELS[o.priority]}</Badge>
                        <Badge variant="outline">{STATUS_LABELS[o.status]}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function PgaMonitoringDetailDialog({ monitoringId, onClose }) {
  const [m, setM] = useState(null);
  useEffect(() => {
    if (monitoringId) {
      setM(null);
      api.get(`/pga/monitorings/${monitoringId}`).then(({ data }) => setM(data)).catch(() => onClose());
    }
  }, [monitoringId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open={!!monitoringId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto scrollbar-thin" data-testid="pga-monitoring-detail-dialog">
        <DialogHeader><DialogTitle className="font-heading">Detalhe do Monitoramento PGA</DialogTitle></DialogHeader>
        {!m ? <div className="py-10 flex justify-center"><div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div> : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline">{m.type_name}</Badge>
              <Badge variant="outline">{m.nc_count} NC</Badge>
              <Badge variant="outline" className="bg-yellow-500/10 text-yellow-500 border-yellow-500/30">{m.captures_total} captura(s)</Badge>
              <span className="text-xs text-muted-foreground">{m.structure_name} · {m.inspector_name} · {formatDate(m.finished_at)}</span>
            </div>
            {(m.temperature != null || m.humidity != null) && (
              <div className="flex gap-3 text-xs text-muted-foreground">
                {m.temperature != null && <span className="flex items-center gap-1"><Thermometer size={12} className="text-orange-400" /> {m.temperature}°C</span>}
                {m.humidity != null && <span className="flex items-center gap-1"><Droplets size={12} className="text-sky-400" /> {m.humidity}% UR</span>}
              </div>
            )}
            <div className="space-y-2">
              {(m.checklist || []).map((c, i) => {
                const Icon = CHECK_ICON[c.status] || Minus;
                return (
                  <div key={i} className="rounded-md border border-border p-3">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <p className="text-sm flex items-center gap-2"><Icon size={14} className={CHECK_CLS[c.status]} /> {c.label}</p>
                      <div className="flex items-center gap-2">
                        {c.status === "nao_conforme" && <Badge variant="outline" className={PRIORITY_COLORS[c.severity]}>{PRIORITY_LABELS[c.severity]}</Badge>}
                        <span className={`text-xs font-semibold ${CHECK_CLS[c.status]}`}>{CHECK_STATUS_LABELS[c.status]}</span>
                      </div>
                    </div>
                    {c.notes && <p className="text-xs text-muted-foreground mt-1.5">{c.notes}</p>}
                    {c.photos?.length > 0 && <div className="mt-2"><EvidenceGallery photos={c.photos} cols="grid-cols-4 sm:grid-cols-6" testId={`pga-item-photos-${i}`} /></div>}
                  </div>
                );
              })}
            </div>
            {m.captures?.length > 0 && (
              <div className="rounded-md border border-yellow-500/40 bg-yellow-500/5 p-3 space-y-2" data-testid="pga-detail-captures">
                <p className="text-sm font-medium flex items-center gap-2"><Bug size={14} className="text-yellow-500" /> Capturas de pragas ({m.captures_total})</p>
                {m.captures.map((c, i) => (
                  <div key={i} className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{c.quantity}x {c.species_name}</span>
                    {c.trap_type ? ` · ${c.trap_type}` : ""}{c.location ? ` · ${c.location}` : ""}{c.detail ? ` — ${c.detail}` : ""}
                    {c.photos?.length > 0 && <div className="mt-1.5"><EvidenceGallery photos={c.photos} cols="grid-cols-4 sm:grid-cols-6" testId={`pga-capture-photos-${i}`} /></div>}
                  </div>
                ))}
              </div>
            )}
            {m.general_photos?.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Fotos gerais da estrutura</p>
                <EvidenceGallery photos={m.general_photos} cols="grid-cols-4 sm:grid-cols-6" testId="pga-general-photos" />
              </div>
            )}
            {m.notes && <p className="text-sm text-muted-foreground italic">"{m.notes}"</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Md({ text }) {
  return (
    <div className="space-y-1.5 text-sm">
      {String(text).split("\n").map((ln, i) => {
        const t = ln.trim();
        if (!t) return null;
        if (t.startsWith("## ")) return <h4 key={i} className="font-heading font-semibold text-sm text-primary mt-3">{t.slice(3).replace(/\*\*/g, "")}</h4>;
        if (t.startsWith("# ")) return <h4 key={i} className="font-heading font-semibold mt-3">{t.slice(2).replace(/\*\*/g, "")}</h4>;
        if (t.startsWith("- ") || t.startsWith("* ") || t.startsWith("• ")) return <p key={i} className="pl-3 text-muted-foreground">• {t.slice(2).replace(/\*\*/g, "")}</p>;
        return <p key={i} className="text-muted-foreground">{t.replace(/\*\*/g, "").replace(/^#+\s*/, "")}</p>;
      })}
    </div>
  );
}

export function PgaAiAnalysisDialog({ open, onOpenChange, clientId, unitId }) {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/pga/ai-analysis", {
        client_id: clientId !== "todos" ? clientId : null,
        unit_id: unitId !== "todas" ? unitId : null,
      });
      setAnalysis(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto scrollbar-thin" data-testid="pga-ai-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Sparkles size={17} className="text-primary" /> Análise Técnica Inteligente — Pragas em Grãos Armazenados
          </DialogTitle>
        </DialogHeader>
        {!analysis ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              A IA analisará os índices de infestação por estrutura, capturas por espécie, temperatura, umidade e tendências para gerar: situação geral, espécies dominantes, estruturas críticas, riscos para a massa de grãos e recomendações de manejo (expurgo, aeração, higienização).
            </p>
            <Button className="w-full" onClick={generate} disabled={loading} data-testid="pga-ai-generate-btn">
              <Sparkles size={15} className="mr-2" /> {loading ? "Analisando dados de monitoramento..." : "Gerar Análise IA"}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div data-testid="pga-ai-content"><Md text={analysis.content} /></div>
            <p className="text-[11px] text-muted-foreground border-t border-border pt-3">
              Análise gerada por IA em {formatDate(analysis.created_at)} por solicitação de {analysis.created_by}. As sugestões são apoio à decisão — a palavra final é do responsável técnico.
            </p>
            <Button variant="outline" className="w-full" onClick={generate} disabled={loading} data-testid="pga-ai-regenerate-btn">
              {loading ? "Analisando..." : "Gerar nova análise"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
